import React, { useMemo } from 'react';

export interface MiniCalendarEventDay {
  dateISO: string; // YYYY-MM-DD
  count: number;
}

interface MiniMonthCalendarProps {
  currentMonth: Date;
  onMonthChange: (newMonth: Date) => void;
  selectedDate?: string; // YYYY-MM-DD
  onSelectDate?: (dateISO: string) => void;
  eventDays?: MiniCalendarEventDay[];
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function formatISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Abreviações únicas (Dom–Sáb); uma letra só gera keys duplicadas no React (Q, S).
const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const MiniMonthCalendar: React.FC<MiniMonthCalendarProps> = ({
  currentMonth,
  onMonthChange,
  selectedDate,
  onSelectDate,
  eventDays = [],
}) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const dayCells = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of eventDays) map[e.dateISO] = e.count;

    const startWeekDay = monthStart.getDay();
    const daysInMonth = monthEnd.getDate();

    const cells: Array<{ key: string; label: string; dateISO?: string; isOther?: boolean; hasEvents?: boolean; count?: number; isSelected?: boolean }>
      = [];

    // Leading blanks from previous month
    for (let i = 0; i < startWeekDay; i++) {
      cells.push({ key: `lead-${i}`, label: '' , isOther: true });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d);
      const iso = formatISO(date);
      const count = map[iso];
      cells.push({
        key: `day-${iso}`,
        label: String(d),
        dateISO: iso,
        hasEvents: typeof count === 'number' && count > 0,
        count,
        isSelected: selectedDate === iso,
      });
    }

    // Trailing blanks to complete grid (up to 6 rows)
    const remainder = cells.length % 7;
    const trailing = remainder === 0 ? 0 : 7 - remainder;
    for (let i = 0; i < trailing; i++) {
      cells.push({ key: `trail-${i}`, label: '' , isOther: true });
    }

    return cells;
  }, [currentMonth, monthStart, monthEnd, eventDays, selectedDate]);

  const goPrevMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const goNextMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(currentMonth);

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: 12,
      backgroundColor: 'white',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button onClick={goPrevMonth} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 6 }}>‹</button>
        <div style={{ fontWeight: 600, textTransform: 'capitalize', color: '#374151' }}>{monthLabel}</div>
        <button onClick={goNextMonth} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 6 }}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {WEEK_DAYS.map((d, index) => (
          <div key={`weekday-${index}`} style={{ textAlign: 'center', fontSize: 11, color: '#6b7280' }}>{d}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {dayCells.map(cell => {
          const baseStyle: React.CSSProperties = {
            borderRadius: 6,
            height: 34,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            cursor: cell.dateISO && onSelectDate ? 'pointer' : 'default',
            border: cell.isSelected ? '2px solid #9333ea' : '1px solid #e5e7eb',
            background: cell.hasEvents ? '#f5f3ff' : '#ffffff',
            color: cell.isOther ? '#d1d5db' : '#374151',
          };
          return (
            <div
              key={cell.key}
              style={baseStyle}
              onClick={() => cell.dateISO && onSelectDate && onSelectDate(cell.dateISO!)}
              title={cell.hasEvents ? `${cell.count} agendamento(s)` : undefined}
            >
              <span>{cell.label}</span>
              {cell.hasEvents && (
                <span style={{
                  marginLeft: 6,
                  fontSize: 10,
                  background: '#9333ea',
                  color: 'white',
                  borderRadius: 999,
                  padding: '0 6px'
                }}>{cell.count}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MiniMonthCalendar;


